// Shared employee-related constants and helpers
// Extracted from EmployeesPage.jsx to keep files small and reusable.

export const EMPLOYEE_TYPES = {
  administratif: { label: "Administratif", color: "bg-blue-100 text-blue-800" },
  commercial: { label: "Commercial", color: "bg-green-100 text-green-800" },
  ouvrier: { label: "Ouvrier", color: "bg-orange-100 text-orange-800" },
};

export const ABSENCE_TYPES = {
  cp: { label: "Congés payés", color: "bg-sky-100 text-sky-800" },
  rtt_salarie: { label: "RTT (salarié)", color: "bg-cyan-100 text-cyan-800" },
  rtt_entreprise: { label: "RTT (entreprise)", color: "bg-teal-100 text-teal-800" },
  maladie: { label: "Maladie", color: "bg-red-100 text-red-800" },
  intemperies: { label: "Intempéries", color: "bg-slate-200 text-slate-800" },
  justifiee: { label: "Absence justifiée", color: "bg-amber-100 text-amber-800" },
  injustifiee: { label: "Absence injustifiée", color: "bg-rose-100 text-rose-800" },
};

export const BONUS_TYPES = {
  noel: "Prime de Noël",
  objectif: "Prime d'objectif",
  exceptionnelle: "Prime exceptionnelle",
  mousse: "Prime de mousse",
  divers: "Divers",
};

export const PV_TYPES = {
  vitesse: "Vitesse",
  stationnement: "Stationnement",
  autre: "Autre",
};

export const SIZES_TOP = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "custom"];
export const SIZES_BOTTOM = ["36", "38", "40", "42", "44", "46", "48", "50", "52", "54", "custom"];
export const SHOE_SIZES = ["36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48"];

export const defaultEmployeeForm = {
  first_name: "",
  last_name: "",
  phone: "",
  address: "",
  size_top: "",
  size_top_custom: "",
  size_bottom: "",
  size_bottom_custom: "",
  shoe_size: "",
  employee_type: "ouvrier",
  comments: "",
  entry_date: "",
  exit_date: "",
  cp_balance: 25,
  rtt_salarie_balance: 8.5,
  rtt_entreprise_balance: 8.5,
  is_interim: false,
  interim_hourly_rate: "",
  interim_agency_name: "",
  interim_agency_phone: "",
  interim_agency_email: "",
  interim_agency_address: "",
};

/**
 * Build sorted timeline entries from multiple data sources, applying the given filter.
 * Each entry has: key, date, time?, category, color, label, title, description.
 */
export function buildTimelineEntries({
  absences = [],
  bonuses = [],
  pvs = [],
  trainings = [],
  lifeEvents = [],
  savs = [],
  orders = [],
  filter,
  formatCurrency,
}) {
  const entries = [];
  const wanted = filter?.types || {};

  if (wanted.absence !== false) {
    (absences || []).forEach((a) => {
      const t = ABSENCE_TYPES[a.absence_type] || { label: a.absence_type, color: "bg-slate-100" };
      entries.push({
        key: `abs-${a.id}`,
        date: a.start_date,
        category: "absence",
        color: t.color,
        label: t.label,
        title: `Absence : ${t.label}`,
        description: `${new Date(a.start_date).toLocaleDateString("fr-FR")} → ${new Date(a.end_date).toLocaleDateString("fr-FR")}${a.is_half_day ? " (½ journée)" : ""}${a.reason ? ` — ${a.reason}` : ""}`,
      });
    });
  }
  if (wanted.bonus !== false) {
    (bonuses || []).forEach((b) => {
      entries.push({
        key: `bon-${b.id}`,
        date: b.bonus_date,
        category: "bonus",
        color: "bg-green-100 text-green-800",
        label: `Prime • ${BONUS_TYPES[b.bonus_type] || b.bonus_type}`,
        title: `Prime : ${BONUS_TYPES[b.bonus_type] || b.bonus_type}`,
        description: `+${formatCurrency(b.amount || 0)}${b.description ? ` — ${b.description}` : ""}`,
      });
    });
  }
  if (wanted.pv !== false) {
    (pvs || []).forEach((p) => {
      entries.push({
        key: `pv-${p.id}`,
        date: p.pv_date,
        category: "pv",
        color: "bg-red-100 text-red-800",
        label: `PV • ${PV_TYPES[p.pv_type] || p.pv_type}`,
        title: `PV : ${PV_TYPES[p.pv_type] || p.pv_type}`,
        description: `-${formatCurrency(p.amount || 0)}${p.description ? ` — ${p.description}` : ""}`,
      });
    });
  }
  if (wanted.training !== false) {
    (trainings || []).forEach((tr) => {
      entries.push({
        key: `tr-${tr.id}`,
        date: tr.training_date,
        category: "training",
        color: "bg-blue-100 text-blue-800",
        label: "Formation",
        title: `Formation : ${tr.name}`,
        description: `${tr.organization || ""}${tr.expiry_date ? ` • expire le ${new Date(tr.expiry_date).toLocaleDateString("fr-FR")}` : ""}${tr.notes ? ` — ${tr.notes}` : ""}`,
      });
    });
  }
  if (wanted["life-event"] !== false) {
    (lifeEvents || []).forEach((ev) => {
      const s = ev.sentiment || "neutral";
      const color =
        s === "positive" ? "bg-emerald-100 text-emerald-800" :
        s === "negative" ? "bg-red-100 text-red-800" :
        "bg-amber-100 text-amber-800";
      const prefix = s === "positive" ? "😊 " : s === "negative" ? "😕 " : "";
      entries.push({
        key: `life-${ev.id}`,
        date: ev.event_date,
        time: ev.event_time,
        category: "life-event",
        color,
        label: `${prefix}${ev.title || "Événement marquant"}`,
        title: ev.title || "Événement marquant",
        description: ev.comment,
      });
    });
  }
  if (wanted.sav !== false) {
    (savs || []).forEach((s) => {
      entries.push({
        key: `sav-${s.id}`,
        date: s.intervention_date,
        category: "sav",
        color: "bg-purple-100 text-purple-800",
        label: `SAV${s.status ? ` • ${s.status}` : ""}`,
        title: `SAV : ${s.client_name || ""}${s.order_reference ? ` (${s.order_reference})` : ""}`,
        description: `${s.cost ? formatCurrency(s.cost) + " — " : ""}${s.description || ""}`.trim(),
      });
    });
  }
  if (wanted.chantier !== false) {
    (orders || []).forEach((o) => {
      const days = Array.isArray(o.days) ? o.days : [];
      entries.push({
        key: `order-${o.order_id}`,
        date: o.first_date,
        category: "chantier",
        color: "bg-indigo-100 text-indigo-800",
        label: `Chantier${o.reference ? ` • ${o.reference}` : ""}`,
        title: `Chantier : ${o.client_name || o.reference || ""}`,
        description: `${o.days_worked} jour(s) • ${o.total_hours} h${o.city ? ` — ${o.city}` : ""}${o.first_date !== o.last_date ? ` (du ${new Date(o.first_date).toLocaleDateString("fr-FR")} au ${new Date(o.last_date).toLocaleDateString("fr-FR")})` : ""}`,
        meta: { days, order_id: o.order_id, reference: o.reference, total_hours: o.total_hours, days_worked: o.days_worked, time_delta: o.time_delta },
      });
    });
  }

  // Period filter (inclusive)
  const fromTs = filter?.from ? new Date(filter.from).getTime() : null;
  const toTs = filter?.to ? new Date(filter.to).getTime() + 86400000 - 1 : null;
  const filtered = entries.filter((ev) => {
    if (!ev.date) return fromTs == null && toTs == null;
    const t = new Date(ev.date).getTime();
    if (fromTs != null && t < fromTs) return false;
    if (toTs != null && t > toTs) return false;
    return true;
  });

  filtered.sort((a, b) => {
    const aKey = `${a.date || ""}T${a.time || "00:00"}`;
    const bKey = `${b.date || ""}T${b.time || "00:00"}`;
    return bKey.localeCompare(aKey);
  });
  return filtered;
}
